import { supabase } from '@/lib/supabase';
import { callApi } from '@/utils/api';
import {
  getActiveTeamQueryKey,
  getTeamInvitesQueryKey,
  getTeamMembersQueryKey,
} from '@/utils/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TeamRole } from './useTeamMembers';
import { Team } from './useTeams';

export type TeamInvite = {
  id: string;
  team_id: string;
  email: string;
  name: string;
  role: Exclude<TeamRole, 'OWNER'>;
  expires_at: string;
  created_at: string;
  team: Team;
};

type InviteTeamMemberData = {
  name: string;
  email: string;
  role: Exclude<TeamRole, 'OWNER'>;
};

export const useTeamInvites = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: getTeamInvitesQueryKey(),
    queryFn: async (): Promise<TeamInvite[]> => {
      const { data, error } = await callApi(supabase, '/teams/invites');

      if (error) throw error;

      return data;
    },
  });

  const inviteTeamMember = useMutation({
    mutationFn: async (data: InviteTeamMemberData): Promise<TeamInvite> => {
      const { data: invite, error } = await callApi(
        supabase,
        '/teams/invites',
        {
          method: 'POST',
          data,
        },
      );

      if (error) throw error;
      if (!invite) throw new Error('Failed to create invite');

      return invite;
    },
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: getTeamMembersQueryKey() }),
  });

  const deleteInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await callApi(supabase, `/teams/invites/${inviteId}`, {
        method: 'DELETE',
      });
      if (error) throw error;
    },
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: getTeamMembersQueryKey() }),
  });

  const acceptInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      const { data, error } = await callApi(
        supabase,
        `/teams/invites/${inviteId}/accept`,
        { method: 'POST' },
      );
      if (error) throw error;

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getTeamMembersQueryKey() });
      queryClient.invalidateQueries({ queryKey: getTeamInvitesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getActiveTeamQueryKey() });
    },
  });

  return {
    ...query,
    inviteTeamMember,
    deleteInvite,
    acceptInvite,
  };
};
